'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TenantBrandHeader } from '@/components/auth/tenant-brand-header';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    console.log('Login page hydrated successfully');
  }, []);

  const doLogin = async (emailVal: string, passwordVal: string) => {
    console.log('Attempting login with:', { email: emailVal, password: '***' });
    setError('');
    setLoading(true);

    try {
      console.log('Calling signIn...');
      const result = await signIn('credentials', {
        email: emailVal,
        password: passwordVal,
        loginPath: 'office',
        redirect: false,
      });
      console.log('signIn result:', result);

      if (result?.error) {
        console.error('Login error:', result.error);
        // FF-542 — deactivated account gets its own message.
        setError(result.code === 'deactivated' ? 'Akun dinonaktifkan. Hubungi admin.' : 'Invalid email or password');
        setLoading(false);
      } else {
        console.log('Login successful, redirecting...');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Login exception:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  // Staff use username + PIN on /staff-login; a username typed here (no "@")
  // is the classic wrong-door mistake → point them there with it prefilled.
  const looksLikeUsername = email.trim().length > 0 && !email.includes('@');
  const staffHref = looksLikeUsername ? `/staff-login?u=${encodeURIComponent(email.trim().toLowerCase())}` : '/staff-login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form onSubmit triggered');
    if (looksLikeUsername) {
      setError('');
      return; // the staff hint under the field explains where to go
    }

    // Get values directly from form elements to handle browser autofill
    const form = formRef.current;
    if (form) {
      const emailInput = form.querySelector<HTMLInputElement>('#email');
      const passwordInput = form.querySelector<HTMLInputElement>('#password');
      const emailVal = emailInput?.value || email;
      const passwordVal = passwordInput?.value || password;
      await doLogin(emailVal, passwordVal);
    } else {
      await doLogin(email, password);
    }
  };

  const handleButtonClick = () => {
    console.log('Button clicked directly');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <TenantBrandHeader />
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Sign in</CardTitle>
            <CardDescription className="text-center">
              Enter your credentials to continue
            </CardDescription>
          </CardHeader>
        <CardContent>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="text"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
              />
              {looksLikeUsername && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900">
                  Ini username staff?{' '}
                  <Link href={staffHref} className="font-semibold text-emerald-700 underline">
                    Pakai Login Staff →
                  </Link>
                  <div className="text-xs text-amber-800 mt-0.5">Halaman ini untuk email kantor. Staff outlet masuk dengan username + PIN.</div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-xs text-indigo-600 hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="w-full h-9 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
              disabled={loading}
              onClick={handleButtonClick}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="relative py-1 text-center text-xs text-gray-400">
              <span className="bg-white px-2 relative z-10">atau</span>
              <div className="absolute inset-x-0 top-1/2 border-t border-gray-200" />
            </div>
            <Link
              href={staffHref}
              className="flex w-full h-10 items-center justify-center rounded-md border-2 border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
            >
              Staff outlet? Login pakai username + PIN →
            </Link>
          </form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
